import type { Match } from '@hooks/use-search-by-bounding-box';
import { quickFiltersSelector } from '@redux/experiments/selectors/quick-filters';
import { useAppSelector } from '@redux/hooks';
import type { AppliedFilterPillProps } from '@typings/search';
import { Column } from '@xo-union/tk-component-grid';
import React, { type FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { MapViewButton } from '../../../MapView';
import { QuickFilters } from '../../../QuickFilters/components/quick-filters';
import PillMore from '../../components/PillMore';
import ClearFiltersLink from '../AppliedFilters/components/ClearFiltersLink';
import FilterPillCategory from './filterPillCategory';
import * as utils from './utils';

export type StateProps = ReturnType<typeof mapStateToProps>;

export interface OwnProps {
	isSticky: boolean;
}

interface MapViewButtonProps {
	match: Match;
	searchLocation: string;
	showMapView: boolean;
}

type Props = StateProps & OwnProps & MapViewButtonProps;

const QuickFilterPillLayout = ({
	showMapView,
	sidebarCategories,
	isMobile,
}: FilterPillLayoutProps) => {
	const pillCategories = ['Distance', 'Price Range'];

	return (
		<>
			{sidebarCategories.length > 0 && <PillMore showMapView={showMapView} />}
			{!isMobile &&
				pillCategories.map((category) => (
					<FilterPillCategory key={category} filterCategory={category} />
				))}
			<QuickFilters />
		</>
	);
};

interface FilterPillLayoutProps {
	filterCategories: string[];
	isMobile: boolean;
	sidebarCategories: string[];
	showMapView: boolean;
	isSticky: boolean;
	appliedFilters: AppliedFilterPillProps[];
}

const FilterPillLayout = ({
	filterCategories,
	isMobile,
	sidebarCategories,
	showMapView,
	isSticky,
	appliedFilters,
}: FilterPillLayoutProps) => {
	const pills = filterCategories.map((filterCategory) => (
		<FilterPillCategory key={filterCategory} filterCategory={filterCategory} />
	));

	return (
		<>
			{!isMobile && <FilterPillCategory key="sort" filterCategory="Sort" />}
			{pills}
			{sidebarCategories.length > 0 && <PillMore showMapView={showMapView} />}
			{appliedFilters.length > 0 && isSticky && !isMobile && (
				<ClearFiltersLink />
			)}
		</>
	);
};

const PillLayout = (props: FilterPillLayoutProps) => {
	const quickFiltersAssignment = useAppSelector((state) =>
		quickFiltersSelector(state),
	);

	if (quickFiltersAssignment === 'quick-filters') {
		return <QuickFilterPillLayout {...props} />;
	}
	return <FilterPillLayout {...props} />;
};

export const FilterPills: FunctionComponent<Props> = (props) => {
	const {
		appliedFilters,
		filterCategories,
		isMobile,
		sidebarCategories,
		isSticky,
		match,
		searchLocation,
		showMapView,
	} = props;

	return (
		<Column xs="12">
			<div
				className={utils.createClassName(isSticky, showMapView)}
				data-map-view={showMapView}
			>
				<PillLayout
					isMobile={isMobile}
					isSticky={isSticky}
					filterCategories={filterCategories}
					sidebarCategories={sidebarCategories}
					appliedFilters={appliedFilters}
					showMapView={showMapView}
				/>
				{showMapView && (
					<MapViewButton match={match} searchLocation={searchLocation} />
				)}
			</div>
		</Column>
	);
};

export const mapStateToProps = (state: Redux.State) => {
	return {
		appliedFilters: state.search.filterPills.applied,
		filterCategories: state.search.filterPills.categories,
		isMobile: state.viewport.isMobile,
		sidebarCategories: state.search.filterPills.sidebar.categories,
	};
};

export default connect<StateProps, OwnProps>(mapStateToProps)(FilterPills);
